import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeftIcon, CopyIcon, QrCodeIcon } from "@phosphor-icons/react";
import api, { resolveUrl } from "@/lib/api";
import Layout from "@/components/Layout";
import PinAnalytics from "@/components/PinAnalytics";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

const ST = { active: "border-primary/40 text-primary", pending_review: "border-accent/40 text-accent", unclaimed: "border-white/25 text-white/50" };

export default function CustomerPinDetail() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [tag, setTag] = useState(null);
  const [stats, setStats] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/portal/tags/${code}`);
      setTag(data);
      const a = await api.get(`/portal/tags/${code}/analytics`);
      setStats(a.data);
    } catch { toast.error("Could not load this pin"); navigate("/"); }
  }, [code, navigate]);

  useEffect(() => { load(); }, [load]);

  if (!tag) return <Layout><p className="font-mono text-sm text-primary animate-pulse" data-testid="pin-detail-loading">LOADING…</p></Layout>;

  const scanUrl = resolveUrl(tag.code);

  return (
    <Layout>
      <button data-testid="pin-detail-back" onClick={() => navigate("/")} className="flex items-center gap-1.5 font-mono text-xs text-white/50 hover:text-primary transition-colors mb-6">
        <ArrowLeftIcon weight="bold" /> YOUR D CODES
      </button>

      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="font-heading font-black text-4xl tracking-tighter leading-none">{tag.label || "Your pin"}</h1>
            <Badge variant="outline" className={`font-mono text-[10px] uppercase ${ST[tag.status] || ""}`} data-testid="pin-detail-status">{tag.status}</Badge>
          </div>
          <p className="font-mono text-primary text-sm mt-2">/{tag.code}</p>
        </div>
        {tag.status === "active" && (
          <a href={scanUrl} target="_blank" rel="noreferrer" data-testid="pin-detail-live" className="inline-flex items-center gap-1.5 border border-primary/40 text-primary px-4 py-2.5 text-xs font-mono hover:bg-primary/10 transition-colors"><QrCodeIcon weight="bold" /> OPEN LIVE LINK</a>
        )}
      </div>

      <div className="border border-border bg-card flex items-center justify-between gap-4 px-6 py-4 mb-4">
        <div className="min-w-0">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40 mb-1">Permanent scan URL · one code, forever</p>
          <p data-testid="pin-detail-scanurl" className="font-mono text-sm truncate text-white/80">{scanUrl}</p>
        </div>
        <Button data-testid="pin-detail-copy" variant="outline" size="sm" onClick={() => { navigator.clipboard.writeText(scanUrl); toast.success("Copied"); }} className="border-border bg-transparent hover:bg-secondary hover:text-white font-mono text-xs shrink-0">
          <CopyIcon weight="bold" className="mr-1.5" /> COPY
        </Button>
      </div>
      {tag.destination_url && (
        <div className="border border-border bg-card px-6 py-4 mb-8">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40 mb-1">Points to</p>
          <p data-testid="pin-detail-destination" className="font-mono text-sm truncate text-white/70">{tag.destination_url}</p>
        </div>
      )}

      <PinAnalytics stats={stats} />
    </Layout>
  );
}
